import { useState, useEffect } from 'react';
import { Calendar, Clock, User, MapPin, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { calendarService } from '@/services/calendarService';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type { Booking } from '@/types';

interface TodaysFocusProps {
  providerId: string;
  onBookingClick?: (booking: Booking) => void;
  onViewAll?: () => void;
}

export function TodaysFocus({ providerId, onBookingClick, onViewAll }: TodaysFocusProps) {
  const [todaysBookings, setTodaysBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTodaysBookings();
  }, [providerId]);

  const loadTodaysBookings = async () => {
    setIsLoading(true);
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      const allBookings = await calendarService.getProviderBookings(providerId, todayStr, todayStr);
      
      // Sort by start time
      const sorted = allBookings.sort((a, b) => {
        const timeA = a.startTime.split(':').map(Number);
        const timeB = b.startTime.split(':').map(Number);
        return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
      });
      
      setTodaysBookings(sorted);
    } catch (error) {
      console.error('Failed to load today\'s bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: Booking['status']) => {
    switch (status) {
      case 'CONFIRMED': return 'text-green-800 bg-green-100 border-green-200';
      case 'PENDING': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'IN_PROGRESS': return 'text-blue-800 bg-blue-100 border-blue-200';
      case 'COMPLETED': return 'text-purple-800 bg-purple-100 border-purple-200';
      case 'CANCELLED': return 'text-red-800 bg-red-100 border-red-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status: Booking['status']) => {
    switch (status) {
      case 'CONFIRMED':
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5" />;
      case 'IN_PROGRESS':
        return <Clock className="w-5 h-5" />;
      default:
        return <Calendar className="w-5 h-5" />;
    }
  };

  const getUpcomingBooking = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    return todaysBookings.find(booking => {
      const [hours, minutes] = booking.startTime.split(':').map(Number);
      const bookingTime = hours * 60 + minutes;
      return bookingTime > currentTime && ['CONFIRMED', 'PENDING'].includes(booking.status);
    });
  };

  const getCurrentBooking = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    return todaysBookings.find(booking => {
      const [startHours, startMinutes] = booking.startTime.split(':').map(Number);
      const [endHours, endMinutes] = booking.endTime.split(':').map(Number);
      const startTime = startHours * 60 + startMinutes;
      const endTime = endHours * 60 + endMinutes;
      return currentTime >= startTime && currentTime < endTime && booking.status === 'IN_PROGRESS';
    });
  };

  const getCompletedCount = () => {
    return todaysBookings.filter(b => b.status === 'COMPLETED').length;
  };

  const getPendingCount = () => {
    return todaysBookings.filter(b => ['PENDING', 'CONFIRMED'].includes(b.status)).length;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const upcomingBooking = getUpcomingBooking();
  const currentBooking = getCurrentBooking();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-6 h-6" />
            Today's Focus
          </h2>
          <p className="text-gray-600 mt-1">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Button variant="outline" onClick={onViewAll}>
          View All Bookings
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Bookings</p>
              <p className="text-3xl font-bold text-blue-900 mt-1">{todaysBookings.length}</p>
            </div>
            <Calendar className="w-12 h-12 text-blue-400" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Completed</p>
              <p className="text-3xl font-bold text-green-900 mt-1">{getCompletedCount()}</p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-400" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Pending</p>
              <p className="text-3xl font-bold text-yellow-900 mt-1">{getPendingCount()}</p>
            </div>
            <Clock className="w-12 h-12 text-yellow-400" />
          </div>
        </Card>
      </div>

      {/* Current Booking */}
      {currentBooking && (
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wide">In Progress Now</span>
              </div>
              <h3 className="text-xl font-bold mb-3">{currentBooking.serviceType}</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {currentBooking.customerName}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {currentBooking.startTime} - {currentBooking.endTime}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {currentBooking.location}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="bg-white text-blue-600 hover:bg-blue-50 border-white"
              onClick={() => onBookingClick?.(currentBooking)}
            >
              View Details
            </Button>
          </div>
        </Card>
      )}

      {/* Upcoming Booking */}
      {upcomingBooking && !currentBooking && (
        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wide">Next Up</span>
              </div>
              <h3 className="text-xl font-bold mb-3">{upcomingBooking.serviceType}</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {upcomingBooking.customerName}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {upcomingBooking.startTime} - {upcomingBooking.endTime}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {upcomingBooking.location}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="bg-white text-emerald-600 hover:bg-emerald-50 border-white"
              onClick={() => onBookingClick?.(upcomingBooking)}
            >
              View Details
            </Button>
          </div>
        </Card>
      )}

      {/* All Today's Bookings */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Today's Schedule</h3>
        
        {todaysBookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings today</h3>
            <p className="text-gray-600">You have a free day! Enjoy your time off.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todaysBookings.map((booking) => (
              <div
                key={booking.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                  booking.id === currentBooking?.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={() => onBookingClick?.(booking)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${getStatusColor(booking.status)}`}>
                      {getStatusIcon(booking.status)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{booking.serviceType}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(booking.status)}`}>
                          {booking.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="grid sm:grid-cols-3 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {booking.startTime} - {booking.endTime}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {booking.customerName}
                        </div>
                        <div className="flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{booking.location}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {booking.estimatedCost && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Estimated</p>
                      <p className="text-lg font-semibold text-green-600">€{booking.estimatedCost}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
